import cn from "classnames";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { VideoPlayer } from "shared/components/VideoPlayer";
import logo from "assets/images/header-logo.png";
import { Text } from "shared/components/Text";
import { Spinner } from "shared/components/Spinner";
import { LanguageChooser } from "shared/components/LanguageChooser";
import { Toast } from "features/MainPage/Toast";
import { Button } from "shared/components/Button";
import { splitArrayToChunks } from "shared/utils";

import css from "./styles.module.scss";
import { appStore } from "store";
import { getLocations } from "store/selectors";
import { setCity } from "store/actions";

const LOCATION_OPTIONS_CHUNK_SIZE = 3;

const checkIfCityUrlChosenTimestampIsOlderThanFiveMinutes = (
  cityUrlChosenTimestamp
) => {
  return (
    cityUrlChosenTimestamp &&
    Date.now() - cityUrlChosenTimestamp > 60 * 5 * 1000
  );
};

export const LocationsPage = ({ isSuccessInUrl }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { locations } = appStore.useState(getLocations);

  const [toastText, setToastText] = useState("");
  const [requestFailed, setRequestFailed] = useState(false);

  const parsedLocations = useMemo(() => {
    const filteredLocations = Object.values(locations.cities).filter(
      (city) => city.cityOptions?.isInLocationsPage === true
    );

    const splitLocations = splitArrayToChunks({
      array: Object.values(filteredLocations),
      chunkSize: LOCATION_OPTIONS_CHUNK_SIZE,
    });
    return splitLocations;
  }, [locations]);

  const cityUrlSaved = useMemo(() => {
    return {
      cityUrl: window.localStorage.getItem("cityUrl"),
      cityUrlChosenTimestamp: window.localStorage.getItem(
        "cityUrlChosenTimestamp"
      ),
    };
  }, []);

  const isMobile = window.matchMedia("(max-width: 1060px)").matches;
  const videoID = !isMobile
    ? locations.countryOptions.locationBackgroundVideoID
    : locations.countryOptions.locationMobileBackgroundVideoID;

  useEffect(() => {
    if (
      isSuccessInUrl &&
      (!cityUrlSaved.cityUrl ||
        !checkIfCityUrlChosenTimestampIsOlderThanFiveMinutes(
          cityUrlSaved.cityUrlChosenTimestamp
        ))
    ) {
      setToastText(t("shared.success"));
    }
  }, [isSuccessInUrl, cityUrlSaved]);

  useEffect(() => {
    document.title = "Pixel XL";
    if (
      cityUrlSaved.cityUrl &&
      checkIfCityUrlChosenTimestampIsOlderThanFiveMinutes(
        cityUrlSaved.cityUrlChosenTimestamp
      )
    ) {
      const bookingId = searchParams.get("booking_id");
      const personId = searchParams.get("person_id");

      let navigateUrl = cityUrlSaved.cityUrl;
      if (bookingId && personId) {
        navigateUrl += `/cancel?booking_id=${bookingId}&person_id=${personId}`;
      } else if (isSuccessInUrl) {
        navigateUrl += "/success";
      }
      navigate(navigateUrl);
    }
  }, [navigate, cityUrlSaved, isSuccessInUrl, searchParams]);

  return (
    <div className={css.LocationsPage}>
      <div className={css.languageChooserContainer}>
        <LanguageChooser
          borderColor="blue"
          className={css.languageChooser}
          shouldScrollUp={isMobile}
        />
      </div>
      <img src={logo} alt="pixel xl logo" className={css.headerLogo} />

      {toastText && <Toast text={toastText} failed={requestFailed} />}

      <div className={css.iframeContainer}>
        <VideoPlayer videoID={videoID} autoplay={true} />
        <div className={css.iframeBlend} />
      </div>
      <div className={css.buttonsContainer}>
        <Text
          type="white"
          className={css.prompt}
          bold
          secondaryFamily
          size="subTitle"
        >
          {t("locationsPage.exploreNewDimension")}
        </Text>
        {parsedLocations.map((locationRow, i) => (
          <div className={css.locationRow} key={`row ${i}`}>
            {locationRow.map((location, j) => (
              <div
                className={css.buttonContainer}
                key={`locationOption ${i * j + j}`}
              >
                <Button
                  text={location.cityOptions.title}
                  onClick={() => {
                    window.localStorage.setItem(
                      "cityUrl",
                      location.cityOptions.siteUrl
                    );
                    window.localStorage.setItem(
                      "cityUrlChosenTimestamp",
                      Date.now()
                    );
                    navigate(location.cityOptions.siteUrl);
                  }}
                  className={css.button}
                />
                {location.cityOptions.label && (
                  <div className={css.label}>
                    <Text
                      type="whiteBordered"
                      className={css.labelText}
                      bold
                      size="bodyLead"
                    >
                      {location.cityOptions.label}
                    </Text>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
